import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type Folga } from "../api";
import ErrorState from "./ErrorState";
import { formatDate } from "../format";

/** Pílula de status da folga (mesma convenção da avaliação). */
const STATUS_PILL: Record<string, string> = {
  Pendente: "pill-warn",
  Aprovada: "pill-ok",
  Reprovada: "pill-danger",
};

/** Antecedência mínima para solicitar folga: 2 dias corridos. */
const ANTECEDENCIA_DIAS = 2;

/** Menor data permitida (hoje + 2 dias) em YYYY-MM-DD — trava do campo e valor inicial. */
function minFolgaYmd(): string {
  const d = new Date();
  d.setDate(d.getDate() + ANTECEDENCIA_DIAS);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Solicitar Folga (Operacional — colaborador). O colaborador escolhe a data e o
 * motivo; a solicitação entra como PENDENTE na agenda (cor "Folga") e aparece
 * para o gestor em "Avaliar Folga". Abaixo, o histórico das próprias folgas.
 *
 * `somenteLeitura` (cadastro do Portal): o formulário aparece travado e o
 * histórico segue consultável.
 */
export default function SolicitarFolgaView({ somenteLeitura = false }: { somenteLeitura?: boolean }) {
  const [data, setData] = useState<string>(minFolgaYmd());
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [rows, setRows] = useState<Folga[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.folgas.minhas());
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    setOk(null);
    if (!data) {
      setFormError("Escolha a data da folga.");
      return;
    }
    if (data < minFolgaYmd()) {
      setFormError(`A folga deve ser solicitada com no mínimo ${ANTECEDENCIA_DIAS} dias de antecedência.`);
      return;
    }
    setSaving(true);
    try {
      await api.folgas.solicitar(data, motivo.trim());
      setOk("Solicitação enviada! Aguarde a avaliação do gestor.");
      setMotivo("");
      await load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Erro ao solicitar a folga.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="folgas solicitar-folga">
      <form className="solicitar-form form-card" onSubmit={submit}>
        <fieldset className="form-travado" disabled={somenteLeitura}>
          <div className="form-grid">
            <label className="field">
              <span>
                Data da folga <em className="req"> *</em>
                <span className="muted"> — mínimo {ANTECEDENCIA_DIAS} dias de antecedência</span>
              </span>
              <input
                type="date"
                value={data}
                min={minFolgaYmd()}
                onChange={(e) => setData(e.target.value)}
              />
            </label>
            <label className="field field-wide">
              <span>Motivo (opcional)</span>
              <input
                type="text"
                value={motivo}
                placeholder="Ex.: consulta médica, assunto pessoal..."
                onChange={(e) => setMotivo(e.target.value)}
              />
            </label>
          </div>

          {formError && <div className="form-error">{formError}</div>}
          {ok && <div className="form-ok">{ok}</div>}

          <div className="solicitar-foot">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Enviando..." : "Solicitar folga"}
            </button>
          </div>
        </fieldset>
      </form>

      <h3 className="section-title">Minhas solicitações</h3>

      {error ? (
        <ErrorState message={error.message} code={error.code} onRetry={load} />
      ) : (
        <div className="table-wrap">
          {loading ? (
            <div className="empty">Carregando...</div>
          ) : rows.length === 0 ? (
            <div className="empty">Você ainda não solicitou nenhuma folga.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Motivo</th>
                  <th>Status</th>
                  <th>Avaliação do gestor</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => {
                  const status = f.folga_status ?? "Pendente";
                  return (
                    <tr key={f.id}>
                      <td>{formatDate(f.data)}</td>
                      <td>{f.servico || "Folga"}</td>
                      <td>
                        <span className={`pill ${STATUS_PILL[status] ?? "pill-neutral"}`}>{status}</span>
                      </td>
                      <td>
                        {f.folga_status ? (
                          <div className="folga-aval">
                            {f.folga_obs && <span className="folga-motivo">{f.folga_obs}</span>}
                            <span className="muted folga-quem">
                              {f.folga_avaliador ? `por ${f.folga_avaliador}` : ""}
                              {f.folga_avaliado_em ? ` · ${formatDate(f.folga_avaliado_em)}` : ""}
                            </span>
                          </div>
                        ) : (
                          <span className="muted">Aguardando</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
